import React from 'react';
import { Form, Badge } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCog, faPen } from '@fortawesome/free-solid-svg-icons';
import { useTheme } from '../../../context/ThemeContext';
import {
  SettingsCard,
  CardHeader,
  CardTitle,
  CardTitleText,
  PreferenceRow,
  PreferenceInfo,
  PreferenceLabel,
  PreferenceDescription,
  PreferenceEditRow,
  PreferenceEditInfo,
  PreferenceSubText,
  FormSection,
  SaveChangesButton,
  CancelButton,
  ActionButtons,
  StyledSwitch,
  ActionIconButton,
} from '../Styles';
import { CARD_ICON_STYLE, INFO_ICON_STYLE, PREFERENCE_ITEMS } from '../constants';
import type { PreferencesSectionProps } from '../types';

const PreferencesSection: React.FC<PreferencesSectionProps> = ({
  editingSection,
  formData,
  loading,
  profileLoading,
  onEdit,
  onSwitchChange,
  onSave,
  onCancel,
}) => {
  const { isDarkMode } = useTheme();

  return (
    <SettingsCard>
      <CardHeader>
        <CardTitle>
          <FontAwesomeIcon icon={faCog} style={CARD_ICON_STYLE} />
          <CardTitleText level={4}>Preferences</CardTitleText>
        </CardTitle>
        <ActionIconButton onClick={profileLoading ? undefined : onEdit} iconColor="#2D6CCA" $loading={profileLoading}>
          <FontAwesomeIcon icon={faPen} fontSize={14} color={isDarkMode ? '#fff' : '#222222'} />
        </ActionIconButton>
      </CardHeader>

      {editingSection !== 'additional' ? (
        <div>
          {PREFERENCE_ITEMS.map(({ key, icon, label, description }) => (
            <PreferenceRow key={key}>
              <FontAwesomeIcon icon={icon} style={INFO_ICON_STYLE} />
              <PreferenceInfo>
                <PreferenceLabel>{label}</PreferenceLabel>
                <PreferenceDescription>{description}</PreferenceDescription>
              </PreferenceInfo>
              <Badge
                status={formData[key] ? 'success' : 'default'}
                text={formData[key] ? 'Enabled' : 'Disabled'}
              />
            </PreferenceRow>
          ))}
        </div>
      ) : (
        <FormSection>
          {PREFERENCE_ITEMS.map(({ key, icon, label, description }) => (
            <Form.Item key={key} label={label}>
              <PreferenceEditRow>
                <FontAwesomeIcon icon={icon} style={INFO_ICON_STYLE} />
                <PreferenceEditInfo>
                  <PreferenceLabel>{label}</PreferenceLabel>
                  <PreferenceSubText type="secondary">{description}</PreferenceSubText>
                </PreferenceEditInfo>
                <StyledSwitch
                  checked={formData[key]}
                  onChange={(checked) => onSwitchChange(key, checked)}
                />
              </PreferenceEditRow>
            </Form.Item>
          ))}
          <ActionButtons>
            <SaveChangesButton icon={<CheckOutlined />} onClick={onSave} loading={loading}>
              Save Changes
            </SaveChangesButton>
            <CancelButton onClick={onCancel}>Cancel</CancelButton>
          </ActionButtons>
        </FormSection>
      )}
    </SettingsCard>
  );
};

export default React.memo(PreferencesSection);
